import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ForceResetPassword from "@/pages/ForceResetPassword";
import ApiDocsPage from "@/pages/ApiDocsPage";
import ConfirmActionPage from "@/pages/ConfirmActionPage";
import EmployeeApp from "@/pages/employee/EmployeeApp";
import CompanyLayout from "@/pages/company/CompanyLayout";
import AdminLayout from "@/pages/admin/AdminLayout";
import EnvironmentBanner, { ENV_BANNER_HEIGHT_PX } from "@/components/EnvironmentBanner";

const LoadingScreen = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="loading-spinner w-8 h-8" />
      <p className="text-muted-foreground text-sm">Carregando...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_reset_password) return <ForceResetPassword />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/login" replace />;
  return children;
};

const RoleRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) {
    // Unauth → bounce to the apex landing (kredz.com.br) instead of staying
    // on app.kredz.com.br/login. The apex hosts marketing + canonical entry.
    if (typeof window !== "undefined" && window.location.hostname === "app.kredz.com.br") {
      window.location.replace("https://kredz.com.br/");
      return <LoadingScreen />;
    }
    return <Navigate to="/login" replace />;
  }
  if (user.role === "employee") return <Navigate to="/employee" replace />;
  if (user.role === "company_admin") return <Navigate to="/company/dashboard" replace />;
  if (user.role === "admin") return <Navigate to="/admin/dashboard" replace />;
  return <Navigate to="/login" replace />;
};

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme} position="top-right" richColors />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <EnvironmentBanner />
          <div style={{ paddingTop: ENV_BANNER_HEIGHT_PX }}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/docs" element={<ApiDocsPage />} />
              <Route path="/confirm-action" element={<ConfirmActionPage />} />
              <Route path="/employee/*" element={
                <ProtectedRoute roles={["employee"]}><EmployeeApp /></ProtectedRoute>
              } />
              <Route path="/company/*" element={
                <ProtectedRoute roles={["company_admin"]}><CompanyLayout /></ProtectedRoute>
              } />
              <Route path="/admin/*" element={
                <ProtectedRoute roles={["admin"]}><AdminLayout /></ProtectedRoute>
              } />
              <Route path="/" element={<RoleRedirect />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </BrowserRouter>
        <ThemedToaster />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
